import { useLoadFile } from '@Hooks/Documents';
import { type ApiTemplateCustomization, EnumValueType } from '@Model';
import type { BlobInput } from '@oicana/browser';
import { useState, useCallback, useEffect, useMemo } from 'react';

function toNameValuePairs(customizations: ApiTemplateCustomization[]): Record<string, string> {
    const entries = customizations.map((c) => [c.name, c.value]);
    return Object.fromEntries(entries);
}

export function useBlobDatasets(groupedCustomizations: [string, ApiTemplateCustomization[] | undefined][]) {
    const { loadFile, addToCache, removeFromCache } = useLoadFile();
    const [blobs, setBlobDatasets] = useState<Map<string, BlobInput>>();

    const getBlobDataset = useCallback(
        async (items: ApiTemplateCustomization[] | undefined) => {
            if (items === undefined || items.length === 0) {
                return undefined;
            }
            const whereTypeIsFile = items.filter((i) => i.type === EnumValueType.File);
            if (whereTypeIsFile.length !== 1) {
                return undefined;
            }
            const customization = whereTypeIsFile[0];
            const arrayBuffer = await loadFile(customization.value);
            if (arrayBuffer === undefined) {
                return undefined;
            }
            const blobDataset: BlobInput = {
                data: new Uint8Array(arrayBuffer),
                metadata: {},
            };

            return blobDataset;
        },
        [loadFile],
    );

    useEffect(() => {
        let active = true;
        void resolveBlobs();
        return () => {
            active = false;
        };

        async function resolveBlobs() {
            const blobs = new Map<string, BlobInput>();
            for (const [dataset, items] of groupedCustomizations) {
                if (!active) {
                    return;
                }
                const blobDataset = await getBlobDataset(items);
                if (blobDataset === undefined) {
                    continue;
                }
                blobs.set(dataset, blobDataset);
            }
            if (active) {
                setBlobDatasets(blobs);
            }
        }
    }, [groupedCustomizations, getBlobDataset, setBlobDatasets]);

    return {
        blobs,
        addToCache,
        removeFromCache,
    };
}
export function useJsonDatasets(groupedCustomizations: [string, ApiTemplateCustomization[] | undefined][]) {
    return useMemo(() => {
        const jsonDatasets = new Map<string, string>();
        for (const [dataset, items] of groupedCustomizations) {
            const whereTypeNotFile = items?.filter((i) => i.type !== EnumValueType.File);
            if (whereTypeNotFile !== undefined && whereTypeNotFile.length > 0) {
                const stringified = JSON.stringify(toNameValuePairs(whereTypeNotFile));
                jsonDatasets.set(dataset, stringified);
            }
        }
        return jsonDatasets;
    }, [groupedCustomizations]);
}
